import { Bot, ClipboardCheck, DoorClosed, Gauge, MessageSquare, Radar, ScrollText } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { Link } from "react-router-dom"

import type { AgentActivity } from "@/components/home/agent-report"
import { cn } from "@/lib/utils"

/**
 * PRAXA's agents, drawn as they are.
 *
 * Four, and they are not the same kind of thing - which is the point of
 * drawing them side by side. Two wait for you and use a model with tools or a
 * second model; one is deliberately *not* a model; one acts on its own
 * schedule. All four go through the same door to the model, with the same
 * budgets, and read only the signed-in account's data.
 *
 * The daily agent lights up while it is running and stays marked once it has
 * run today. The others light up where they work - on their own pages.
 */

interface AgentCard {
  key: string
  icon: LucideIcon
  name: string
  kind: string
  does: string
  when: string
  to: string
}

const AGENTS: AgentCard[] = [
  {
    key: "chat",
    icon: MessageSquare,
    name: "Chat agent",
    kind: "Model + tools, a loop",
    does: "Decides which tool to call, reads the result, and repeats - at most six rounds. In Chat it has one tool: search your CVs and the guides.",
    when: "When you ask in Chat",
    to: "/chat",
  },
  {
    key: "writer",
    icon: ScrollText,
    name: "Writer + critic",
    kind: "Two models",
    does: "The writer tailors a CV or letter to a posting; a second model with no tools checks every claim against your CV. You approve, or nothing is kept.",
    when: "When you write an application",
    to: "/documents",
  },
  {
    key: "interview",
    icon: ClipboardCheck,
    name: "Interview engine",
    kind: "Rules, not a model",
    does: "A model scores each answer on the rubric; whether to follow up, move on or finish is a threshold on those scores - predictable on purpose.",
    when: "When you practise",
    to: "/interview",
  },
  {
    key: "daily",
    icon: Radar,
    name: "Daily job agent",
    kind: "Acts on a schedule",
    does: "Fetches new postings for your roles, pre-ranks them against your CV for free, scores the closest with the model, and reports the strong ones.",
    when: "Every morning",
    to: "/jobs",
  },
]

const DAILY_LABEL: Record<AgentActivity, string> = {
  running: "Running now",
  ran: "Ran today",
  idle: "Not run yet today",
  off: "Switched off",
}

/** `daily` is the agent's state for the signed-in account; without it (the
 * public How it works page) the card just says when the agent runs. */
export function AgentMap({ daily }: { daily?: AgentActivity }) {
  return (
    <section aria-labelledby="agent-map-title" className="space-y-3">
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">The agents</p>
        <h2 id="agent-map-title" className="mt-1 text-lg font-semibold">
          Four agents, four different jobs
        </h2>
      </div>

      <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {AGENTS.map((agent) => {
          const Icon = agent.icon
          const isDaily = agent.key === "daily"
          const lit = isDaily && daily !== undefined && (daily === "running" || daily === "ran")
          return (
            <li key={agent.key}>
              <Link
                to={agent.to}
                className={cn(
                  "flex h-full flex-col gap-2 rounded-2xl border bg-card/60 p-4 backdrop-blur transition-all hover:-translate-y-0.5 hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  lit && "border-primary/60 shadow-lg shadow-primary/10",
                )}
                data-agent={agent.key}
                data-lit={lit ? "true" : "false"}
              >
                <span className="flex items-center justify-between">
                  <span
                    className={cn(
                      "relative grid size-9 place-items-center rounded-xl bg-primary/10 text-primary",
                    )}
                  >
                    {isDaily && daily === "running" ? (
                      <span
                        aria-hidden
                        className="absolute inset-0 animate-ping rounded-xl bg-primary/30"
                      />
                    ) : null}
                    <Icon className="relative size-4" aria-hidden />
                  </span>
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[10px] font-medium",
                      isDaily && lit
                        ? "bg-primary/15 text-primary"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    {isDaily && daily !== undefined ? DAILY_LABEL[daily] : agent.when}
                  </span>
                </span>
                <span className="font-semibold">{agent.name}</span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  {agent.kind}
                </span>
                <span className="text-sm leading-relaxed text-muted-foreground">{agent.does}</span>
              </Link>
            </li>
          )
        })}
      </ol>

      {/* What all four share. Drawn as the floor under them because it is: no
          agent reaches a model or a row except through these. */}
      <div className="grid gap-3 sm:grid-cols-3">
        <Shared
          icon={DoorClosed}
          title="One door to the model"
          body="Every call goes through one client that checks the budget first and records the cost."
        />
        <Shared
          icon={Gauge}
          title="Spending limits"
          body="A daily allowance per account, a monthly ceiling for the server, and a hard limit on the key."
        />
        <Shared
          icon={Bot}
          title="Only your data"
          body="Each agent reads and writes the signed-in account's rows only - checked in code and again by the database."
        />
      </div>
    </section>
  )
}

function Shared({ icon: Icon, title, body }: { icon: LucideIcon; title: string; body: string }) {
  return (
    <div className="flex gap-3 rounded-xl border border-dashed bg-background/40 p-3">
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{body}</p>
      </div>
    </div>
  )
}
